"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "./Auth";
import VideoScriptGenerator from "./VideoScriptGenerator";
import SentimentAnalyzer from "./SentimentAnalyzer";
import ImageGenerator from "./ImageGenerator";
import PitchGenerator from "./PitchGenerator";
import BlackFridayGenerator from "./BlackFridayGenerator";
import OSINTAnalyzer from "./OSINTAnalyzer";
import PromptSuggestions from "./PromptSuggestions";
import EmailSequenceGenerator from "./EmailSequenceGenerator";
import SalesFunnelGenerator from "./SalesFunnelGenerator";
import LandingPageGenerator from "./LandingPageGenerator";
import AdsCampaignGenerator from "./AdsCampaignGenerator";
import ViralContentGenerator from "./ViralContentGenerator";
import SalesAnalytics from "./SalesAnalytics";
import UserManagement from "./UserManagement";
import { useToast } from "./Toast";

interface AdminPanelContentProps {
  className?: string;
}

export default function AdminPanelContent({ className = "" }: AdminPanelContentProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isMobileTabMenuOpen, setIsMobileTabMenuOpen] = useState(false);
  const { addToast } = useToast();

  // Carregar aba selecionada do localStorage
  useEffect(() => {
    const savedTab = localStorage.getItem('selectedTab');
    if (savedTab) {
      setActiveTab(savedTab);
      setTimeout(() => localStorage.removeItem('selectedTab'), 100);
    }
  }, []);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.mobile-tab-menu') && !target.closest('.mobile-tab-button')) {
        setIsMobileTabMenuOpen(false);
      }
    };

    if (isMobileTabMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileTabMenuOpen]);

  const tabs = [
    { id: "dashboard", name: "Dashboard", icon: "📊", category: "principal" },
    { id: "users", name: "Usuários", icon: "👥", category: "principal" },
    { id: "image", name: "Gerar Imagem", icon: "🎨", category: "conteudo" },
    { id: "pitch", name: "Gerar Pitch", icon: "📝", category: "conteudo" },
    { id: "video", name: "Script de Vídeo", icon: "🎬", category: "conteudo" },
    { id: "viral", name: "Conteúdo Viral", icon: "🚀", category: "conteudo" },
    { id: "prompts", name: "Sugestões IA", icon: "💡", category: "conteudo" },
    { id: "black-friday", name: "Black Friday", icon: "🔥", category: "marketing" },
    { id: "email", name: "Email Sequence", icon: "📧", category: "marketing" },
    { id: "funnel", name: "Funil de Vendas", icon: "🎯", category: "marketing" },
    { id: "landing", name: "Landing Page", icon: "🌐", category: "marketing" },
    { id: "ads", name: "Campanha Ads", icon: "📱", category: "marketing" },
    { id: "sentiment", name: "Análise de Sentimento", icon: "🧠", category: "analise" },
    { id: "osint", name: "OSINT", icon: "🔍", category: "analise" },
    { id: "analytics", name: "Análise de Vendas", icon: "📈", category: "analise" },
    { id: "code", name: "Gerar Código", icon: "💻", category: "tecnico" },
    { id: "translate", name: "Tradutor", icon: "🌍", category: "tecnico" },
    { id: "summarize", name: "Resumidor", icon: "📄", category: "tecnico" },
  ];

  const tabsByCategory = {
    principal: tabs.filter(tab => tab.category === 'principal'),
    conteudo: tabs.filter(tab => tab.category === 'conteudo'),
    marketing: tabs.filter(tab => tab.category === 'marketing'),
    analise: tabs.filter(tab => tab.category === 'analise'),
    tecnico: tabs.filter(tab => tab.category === 'tecnico'),
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-6 rounded-2xl shadow-xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">📊 Dashboard Administrativo</h3>
              <p className="opacity-90">Bem-vindo, {user?.name}!</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white shadow-lg transform transition-all duration-200 hover:scale-105">
                <h4 className="text-lg font-semibold mb-2">Total de Usuários</h4>
                <p className="text-3xl font-bold">2</p>
                <p className="text-sm opacity-90 mt-2">1 Admin, 1 Usuário</p>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white shadow-lg transform transition-all duration-200 hover:scale-105">
                <h4 className="text-lg font-semibold mb-2">Ferramentas IA</h4>
                <p className="text-3xl font-bold">18</p>
                <p className="text-sm opacity-90 mt-2">Funcionalidades Ativas</p>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-xl text-white shadow-lg transform transition-all duration-200 hover:scale-105">
                <h4 className="text-lg font-semibold mb-2">Status do Sistema</h4>
                <p className="text-xl font-bold">🟢 Online</p>
                <p className="text-sm opacity-90 mt-2">100% Operacional</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h4 className="text-lg font-semibold mb-4 text-gray-800">📈 Atividade Recente</h4>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-600 mb-1 sm:mb-0">🚀 Deploy realizado com sucesso</span>
                  <span className="text-xs text-gray-500">Há 5 minutos</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-600 mb-1 sm:mb-0">📱 Navegação mobile otimizada</span>
                  <span className="text-xs text-gray-500">Há 1 hora</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-600 mb-1 sm:mb-0">🔧 Sistema atualizado</span>
                  <span className="text-xs text-gray-500">Há 5 horas</span>
                </div>
              </div>
            </div>
          </div>
        );
      case "users":
        return <UserManagement />;
      case "image":
        return <ImageGenerator />;
      case "pitch":
        return <PitchGenerator />;
      case "video":
        return <VideoScriptGenerator />;
      case "sentiment":
        return <SentimentAnalyzer />;
      case "black-friday":
        return <BlackFridayGenerator />;
      case "osint":
        return <OSINTAnalyzer />;
      case "viral":
        return <ViralContentGenerator />;
      case "analytics":
        return <SalesAnalytics />;
      case "prompts":
        return <PromptSuggestions />;
      case "email":
        return <EmailSequenceGenerator />;
      case "funnel":
        return <SalesFunnelGenerator />;
      case "landing":
        return <LandingPageGenerator />;
      case "ads":
        return <AdsCampaignGenerator />;
      case "code":
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">💻 Gerador de Código</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                <strong>Em desenvolvimento:</strong> Esta funcionalidade estará disponível em breve.
              </p>
            </div>
          </div>
        );
      case "translate":
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">🌍 Tradutor Avançado</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                <strong>Em desenvolvimento:</strong> Esta funcionalidade estará disponível em breve.
              </p>
            </div>
          </div>
        );
      case "summarize":
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">📄 Resumidor de Texto</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                <strong>Em desenvolvimento:</strong> Esta funcionalidade estará disponível em breve.
              </p>
            </div>
          </div>
        );
      default:
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Página em Desenvolvimento</h3>
            <p>Esta funcionalidade está sendo desenvolvida.</p>
          </div>
        );
    }
  };

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header Mobile */}
      <div className="lg:hidden bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsMobileTabMenuOpen(!isMobileTabMenuOpen)}
                className="mobile-tab-button p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{currentTab?.name}</h2>
                <p className="text-xs text-gray-500">👤 {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg">{currentTab?.icon}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tab Menu Overlay */}
      {isMobileTabMenuOpen && (
        <div className="mobile-tab-menu fixed inset-0 z-50 lg:hidden">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsMobileTabMenuOpen(false)}
          />
          
          {/* Slide Menu */}
          <div className="fixed top-0 left-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-out">
            <div className="bg-gradient-to-r from-primary to-primary/90 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">🚀 Axion IA Panel</h2>
                <button
                  onClick={() => setIsMobileTabMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-lg">👤</span>
                </div>
                <div className="ml-3">
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-sm opacity-75">Administrador</div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {Object.entries(tabsByCategory).map(([category, categoryTabs]) => (
                <div key={category} className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    {category === 'principal' && '🏠 Principal'}
                    {category === 'conteudo' && '🎨 Criação de Conteúdo'}
                    {category === 'marketing' && '🔥 Marketing & Vendas'}
                    {category === 'analise' && '🧠 Análise & Inteligência'}
                    {category === 'tecnico' && '⚙️ Ferramentas Técnicas'}
                  </h3>
                  <div className="space-y-1">
                    {categoryTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setIsMobileTabMenuOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 ${
                          activeTab === tab.id
                            ? "bg-primary text-white shadow-md transform scale-105"
                            : "text-gray-700 hover:bg-gray-100 hover:transform hover:translate-x-1"
                        }`}
                      >
                        <span className="mr-3 text-lg">{tab.icon}</span>
                        <span className="font-medium">{tab.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Layout */}
      <div className="hidden lg:flex">
        {/* Sidebar Desktop */}
        <div className="w-64 bg-white shadow-lg min-h-screen sticky top-0">
          <div className="p-6">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center text-white">
                <span className="text-xl">👤</span>
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-bold text-gray-800">{user?.name}</h2>
                <p className="text-sm text-gray-500">Administrador</p>
              </div>
            </div>
            
            <nav className="space-y-1">
              {Object.entries(tabsByCategory).map(([category, categoryTabs]) => (
                <div key={category} className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {category === 'principal' && '🏠 Principal'}
                    {category === 'conteudo' && '🎨 Criação'}
                    {category === 'marketing' && '🔥 Marketing'}
                    {category === 'analise' && '🧠 Análise'}
                    {category === 'tecnico' && '⚙️ Técnico'}
                  </h3>
                  {categoryTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 ${
                        activeTab === tab.id
                          ? "bg-primary text-white shadow-md transform scale-105"
                          : "text-gray-700 hover:bg-gray-100 hover:transform hover:translate-x-1"
                      }`}
                    >
                      <span className="mr-3 text-lg">{tab.icon}</span>
                      <span className="font-medium text-sm">{tab.name}</span>
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content Desktop */}
        <div className="flex-1 p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Mobile Content */}
      <div className="lg:hidden p-4 pb-20">
        <div className="max-w-full">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
